/**
 * Utilitário para cálculo local de status de transações.
 *
 * Replica no frontend a mesma lógica do backend (TransacaoEntity.calcularStatus),
 * permitindo derivar o status em tempo de leitura sem requisitar o servidor.
 * Isso garante que transações lidas do cache local exibam status atualizado
 * mesmo offline.
 *
 * @module status-transacao.util
 */

/**
 * Calcula o status de uma transação localmente (sem requisitar backend).
 *
 * Usado pelo frontend ao ler transações do cache para garantir status atualizado.
 * Aplica a mesma lógica do backend (TransacaoEntity.calcularStatus):
 * - dataPagamento preenchido → "paga" (despesa), "recebida" (receita), "efetivada" (transferência)
 * - dataPagamento nulo + dataVencimento >= hoje → "a_vencer"
 * - dataPagamento nulo + dataVencimento < hoje → "atrasada"
 * - dataPagamento nulo + sem dataVencimento → "a_vencer"
 *
 * @param {Object} transacao - Transação com campos dataPagamento, dataVencimento, tipo
 * @param {string|null} transacao.dataPagamento - Data de pagamento YYYY-MM-DD ou null
 * @param {string|null} transacao.dataVencimento - Data de vencimento YYYY-MM-DD ou null
 * @param {string} transacao.tipo - Tipo: 'receita', 'despesa' ou 'transferencia'
 * @returns {string} Status calculado: 'paga', 'recebida', 'efetivada', 'a_vencer' ou 'atrasada'
 *
 * @example
 * // Despesa paga
 * calcularStatusLocal({ dataPagamento: '2024-01-15', dataVencimento: '2024-01-10', tipo: 'despesa' })
 * // → 'paga'
 *
 * @example
 * // Receita pendente com vencimento futuro
 * calcularStatusLocal({ dataPagamento: null, dataVencimento: '2025-12-31', tipo: 'receita' })
 * // → 'a_vencer'
 *
 * @example
 * // Transferência atrasada
 * calcularStatusLocal({ dataPagamento: null, dataVencimento: '2020-01-01', tipo: 'transferencia' })
 * // → 'atrasada'
 */
export function calcularStatusLocal (transacao) {
  // Se tem dataPagamento, a transação está efetivada (paga/recebida/efetivada conforme tipo)
  if (transacao.dataPagamento) {
    if (transacao.tipo === 'receita') return 'recebida'
    if (transacao.tipo === 'transferencia') return 'efetivada'
    return 'paga'
  }

  // Sem dataVencimento, assume como pendente (a_vencer)
  if (!transacao.dataVencimento) return 'a_vencer'

  // Compara dataVencimento com a data atual (comparação apenas por dia, sem hora)
  const [ano, mes, dia] = transacao.dataVencimento.split('-').map(Number)
  const vencimento = new Date(ano, mes - 1, dia)
  vencimento.setHours(0, 0, 0, 0)

  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)

  return vencimento >= hoje ? 'a_vencer' : 'atrasada'
}
